import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";

import ServiceScriptProvider, { ServiceScript } from "providers/ServicesProvider/ServiceScriptProvider";


const CHARGEBEE_SERVICE_NAME = "Chargebee";
const CHARGEBEE_SERVICE_SCRIPT_NAME = "chargebee-script";



interface ChargebeePortalSections {
  SUBSCRIPTION_DETAILS: string;
  SUBSCRIPTION_CANCELLATION: string;
  EDIT_SUBSCRIPTION: string;
  VIEW_SCHEDULED_CHANGES: string;
  ACCOUNT_DETAILS: string;
  EDIT_ACCOUNT_DETAILS: string;
  ADDRESS: string;
  EDIT_BILLING_ADDRESS: string;
  EDIT_SHIPPING_ADDRESS: string;
  EDIT_SUBSCRIPTION_CUSTOM_FIELDS: string;
  PAYMENT_SOURCES: string;
  ADD_PAYMENT_SOURCE: string;
  EDIT_PAYMENT_SOURCE: string;
  VIEW_PAYMENT_SOURCE: string;
  CHOOSE_PAYMENT_METHOD_FOR_SUBSCRIPTION: string;
  BILLING_HISTORY: string;
}

export type ChargebeePortalSection = keyof ChargebeePortalSections;

export interface ChargebeeContextType {
  chargebeeInstance: any;
  chargebeePortalSections: ChargebeePortalSections;
}

interface IChargebeeProviderProps {
  children: ReactNode;
  publishableKey: string;
  site: string;
  enableGTMTracking?: boolean;
  enableGATracking?: boolean;
}

let ChargebeeContext = createContext<ChargebeeContextType>(null!);

export default function ChargebeeProvider({
  children,
  publishableKey,
  site,
  enableGTMTracking,
  enableGATracking
}: IChargebeeProviderProps) {

  const [ initialized, setInitialized ] = useState(false);

  useEffect(() => {
    if (window.Chargebee !== undefined) {
      setInitialized(true);
      return;
    }

    // Script tag may exist from a prior mount but still be loading.
    // ServiceScriptProvider won't re-create it, so attach our own load listener.
    const existingScript = document.getElementById(CHARGEBEE_SERVICE_SCRIPT_NAME);
    if (existingScript) {
      const onLoad = () => { setInitialized(true); };
      existingScript.addEventListener('load', onLoad);
      // Re-check in case it finished loading between the first check and addEventListener
      if (window.Chargebee !== undefined) {
        setInitialized(true);
      }
      return () => { existingScript.removeEventListener('load', onLoad); };
    }
  }, []);

  const chargebeeInstance = useMemo(() => {
    if (initialized && (window.Chargebee !== undefined)) {
      try {
        return window.Chargebee.getInstance();
      } catch (error) {
        return window.Chargebee.init({
          site,
          publishableKey,
          enableGTMTracking,
          enableGATracking
        });
      }
    } else {
      return null;
    }
  }, [ enableGATracking, enableGTMTracking, publishableKey, site, initialized ]);

  const chargebeeScript: ServiceScript = {
    name: CHARGEBEE_SERVICE_NAME,
    domId: CHARGEBEE_SERVICE_SCRIPT_NAME,
    src: "https://js.chargebee.com/v2/chargebee.js",
    onLoadCallback: () => { setInitialized(true); },
    onUnloadCallback: () => { setInitialized(false); }
  };

  const portalSections = useMemo(() => {
    if (initialized && (window.Chargebee !== undefined)) {
      return window.Chargebee.getPortalSections();
    } else {
      return null;
    }
  }, [ initialized ]);

  let value = {
    chargebeeInstance,
    chargebeePortalSections: portalSections
  };
  return (
    <>
      <ServiceScriptProvider
        { ...chargebeeScript }
      />
      <ChargebeeContext.Provider value={value}>
        {children}
      </ChargebeeContext.Provider>
    </>
  );
}

export function useChargebee() {
  return useContext(ChargebeeContext);
}
